
import { useRef, useEffect, useState, useCallback } from 'react';
import type { BlockRendererProps } from '@/lib/email-builder/block-registry';
import { parseBlockConfiguration } from '@/types/email-blocks';
import type { TextBlockConfiguration } from '@/types/block-configurations';
import { buildTextDecoration, buildFontStyle } from '@/lib/email-builder/text-style-utils';
import { useEmailBuilderStore } from '@/store/emailBuilderStore';
import { useDebouncedUpdate } from '@/hooks/useDebouncedUpdate';
import { cleanPastedContent, stripExecutableContent } from '@/lib/html-sanitizer';
import { toDisplayHtml, toStorageHtml, buildPillHtml, findPillAncestor, sanitizeMergeVarsInHtml } from '@/lib/email-builder/merge-variables';
import { MergeVariableModal } from '@/components/email-builder/MergeVariableModal';

export function TextBlock({ block, isSelected, isHovered, onSelect, onHover }: BlockRendererProps) {
  const config = parseBlockConfiguration(block) as TextBlockConfiguration;

  const patchBlockConfigLocal = useEmailBuilderStore((s) => s.patchBlockConfigLocal);
  const persistBlockConfig = useEmailBuilderStore((s) => s.persistBlockConfig);
  const contentRef = useRef<HTMLDivElement>(null);
  const [isEditing, setIsEditing] = useState(false);
  // Text (storage format) the DOM currently shows. A different config.text means the change came
  // from elsewhere (toolbar, undo, reload) and has to be written into the DOM.
  const shownTextRef = useRef<string | undefined>(undefined);

  // Merge-variable edit state
  const [editingMergeVar, setEditingMergeVar] = useState<{
    element: HTMLSpanElement;
    variable: string;
    fallback?: string;
  } | null>(null);

  // Typing updates the store at once (so the panel, toolbar and undo see the current text) and
  // the server after a pause; the latest configuration is read when sending.
  const { debouncedUpdate, immediateUpdate } = useDebouncedUpdate({
    delay: 500,
    onUpdate: () => {
      persistBlockConfig(block.id).catch((error) => {
        console.error('Failed to save text changes:', error);
      });
    },
  });

  // Show changes made outside this editor (and the initial content)
  useEffect(() => {
    if (!contentRef.current || config.text === shownTextRef.current) return;
    shownTextRef.current = config.text;
    const displayContent = stripExecutableContent(toDisplayHtml(config.text || 'Click to edit text'));
    if (contentRef.current.innerHTML !== displayContent) {
      contentRef.current.innerHTML = displayContent;
    }
  }, [config.text]);

  // Handle content changes
  const handleInput = useCallback(() => {
    if (contentRef.current) {
      // Convert display pills back to storage format
      const storageValue = toStorageHtml(contentRef.current.innerHTML);
      shownTextRef.current = storageValue;
      patchBlockConfigLocal(block.id, { text: storageValue });
      debouncedUpdate(storageValue);
    }
  }, [block.id, debouncedUpdate, patchBlockConfigLocal]);

  // Handle paste events - sanitize pasted content while preserving merge-variable pills
  const handlePasteEvent = useCallback((e: React.ClipboardEvent) => {
    e.preventDefault();
    const clipboardData = e.nativeEvent.clipboardData;
    if (!clipboardData || !contentRef.current) return;

    // Try HTML first (preserves pills that were copied within the editor)
    const html = clipboardData.getData('text/html');
    if (html) {
      const hasPills = /merge-variable-placeholder/.test(html);

      if (hasPills) {
        // Sanitize while preserving merge-variable spans
        const content = sanitizeMergeVarsInHtml(cleanPastedContent(html));
        document.execCommand('insertHTML', false, content);
      } else {
        // Standard sanitization — no pills to preserve
        const content = cleanPastedContent(html);
        document.execCommand('insertHTML', false, content);
      }
      handleInput();
      return;
    }

    // Fallback to plain text
    const text = clipboardData.getData('text/plain');
    if (text) {
      document.execCommand('insertText', false, text);
      handleInput();
    }
  }, [handleInput]);

  // Leaving the editor sends any edit still waiting
  const handleBlur = useCallback(() => {
    setIsEditing(false);
    immediateUpdate();
  }, [immediateUpdate]);

  // Handle double-click to enter edit mode
  const handleDoubleClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setIsEditing(true);
    // Focus the contentEditable element
    setTimeout(() => {
      if (contentRef.current) {
        contentRef.current.focus();
        // Select all text on double-click if it's placeholder text
        if (config.text === '' || config.text === 'Click to edit text') {
          const range = document.createRange();
          range.selectNodeContents(contentRef.current);
          const selection = window.getSelection();
          if (selection) {
            selection.removeAllRanges();
            selection.addRange(range);
          }
        }
      }
    }, 0);
  }, [config.text]);

  // Handle single-click - select block but don't edit
  const handleClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();

    // Check if a merge-variable pill was clicked
    const target = e.target as HTMLElement;
    if (target.classList.contains('merge-variable-placeholder')) {
      e.preventDefault();
      const variable = target.getAttribute('data-variable') || '';
      const fallback = target.getAttribute('data-fallback') || undefined;
      setEditingMergeVar({ element: target as HTMLSpanElement, variable, fallback });
      return;
    }

    onSelect(block.id);
    // If already selected, double-click will enable editing
  }, [block.id, onSelect]);

  // Handle saving an edited merge variable pill
  const handleMergeVarSave = useCallback((variable: string, fallback?: string) => {
    if (!editingMergeVar) return;
    const el = editingMergeVar.element;
    // Build a temporary container to parse the new pill HTML
    const newPillHtml = buildPillHtml(variable, fallback);
    const temp = document.createElement('div');
    temp.innerHTML = newPillHtml;
    const newPill = temp.firstElementChild as HTMLSpanElement;
    if (newPill && el.parentNode) {
      el.parentNode.replaceChild(newPill, el);
      handleInput();
    }
    setEditingMergeVar(null);
  }, [editingMergeVar, handleInput]);

  // Handle removing a merge variable pill
  const handleMergeVarClose = useCallback(() => {
    setEditingMergeVar(null);
  }, []);

  // Keyboard shortcuts + atomic pill deletion
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    // Exit edit mode on Escape
    if (e.key === 'Escape') {
      e.preventDefault();
      contentRef.current?.blur();
      return;
    }
    // Save on Ctrl/Cmd + S
    if ((e.ctrlKey || e.metaKey) && e.key === 's') {
      e.preventDefault();
      immediateUpdate();
      return;
    }

    // --- Atomic pill deletion via Backspace / Delete ---
    if (e.key !== 'Backspace' && e.key !== 'Delete') return;

    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;

    // If the selection already encompasses a pill (e.g. user-select:all selected it), let the
    // browser delete it, then trigger save.
    if (!sel.isCollapsed) {
      // Check if any selected node is a pill — if so let the browser handle it
      // and trigger an input afterwards.
      return;
    }

    const range = sel.getRangeAt(0);
    const { startContainer, startOffset } = range;

    let pillToRemove: HTMLSpanElement | null = null;

    if (e.key === 'Backspace') {
      // Caret is inside or right after a pill?
      pillToRemove = findPillAncestor(startContainer);

      if (!pillToRemove && startContainer.nodeType === Node.TEXT_NODE) {
        // Caret at the beginning of a text node — check previous sibling
        if (startOffset === 0 || (startOffset === 1 && startContainer.textContent?.charAt(0) === '\u200B')) {
          const prev = findPreviousNode(startContainer);
          pillToRemove = prev ? findPillAncestor(prev) : null;
          if (!pillToRemove && prev?.nodeType === Node.ELEMENT_NODE) {
            pillToRemove = (prev as HTMLElement).classList.contains('merge-variable-placeholder')
              ? (prev as HTMLSpanElement)
              : null;
          }
        }
      } else if (!pillToRemove && startContainer.nodeType === Node.ELEMENT_NODE) {
        // Caret between child nodes — look at the node just before the caret
        const nodeBefore = startContainer.childNodes[startOffset - 1];
        if (nodeBefore) {
          pillToRemove = findPillAncestor(nodeBefore);
          if (!pillToRemove && nodeBefore.nodeType === Node.ELEMENT_NODE) {
            pillToRemove = (nodeBefore as HTMLElement).classList.contains('merge-variable-placeholder')
              ? (nodeBefore as HTMLSpanElement)
              : null;
          }
        }
      }
    } else {
      // Delete key
      pillToRemove = findPillAncestor(startContainer);

      if (!pillToRemove && startContainer.nodeType === Node.TEXT_NODE) {
        const text = startContainer.textContent || '';
        if (startOffset >= text.length || (startOffset === text.length - 1 && text.charAt(startOffset) === '\u200B')) {
          const next = findNextNode(startContainer);
          pillToRemove = next ? findPillAncestor(next) : null;
          if (!pillToRemove && next?.nodeType === Node.ELEMENT_NODE) {
            pillToRemove = (next as HTMLElement).classList.contains('merge-variable-placeholder')
              ? (next as HTMLSpanElement)
              : null;
          }
        }
      } else if (!pillToRemove && startContainer.nodeType === Node.ELEMENT_NODE) {
        const nodeAfter = startContainer.childNodes[startOffset];
        if (nodeAfter) {
          pillToRemove = findPillAncestor(nodeAfter);
          if (!pillToRemove && nodeAfter.nodeType === Node.ELEMENT_NODE) {
            pillToRemove = (nodeAfter as HTMLElement).classList.contains('merge-variable-placeholder')
              ? (nodeAfter as HTMLSpanElement)
              : null;
          }
        }
      }
    }

    if (pillToRemove) {
      e.preventDefault();
      pillToRemove.remove();
      handleInput();
    }
  }, [immediateUpdate, handleInput]);

  return (
    <div
      onClick={handleClick}
      onDoubleClick={handleDoubleClick}
      onMouseEnter={(e) => {
        e.stopPropagation();
        onHover(block.id);
      }}
      onMouseLeave={() => onHover(null)}
      className={`
        lw-block-text cursor-pointer rounded transition-all
        ${isSelected ? 'ring-2 ring-primary ring-offset-2' : ''}
        ${isHovered && !isSelected ? 'ring-1 ring-muted-foreground/50' : ''}
        ${isEditing ? 'ring-2 ring-blue-500 ring-offset-2' : ''}
      `}
      title={isEditing ? 'Editing - Press Escape to finish' : 'Double-click to edit text'}
      style={{
        color: config.typography?.color || '#000000',
        fontSize: `${config.typography?.fontSize || 14}px`,
        fontFamily: config.typography?.fontFamily || 'Arial, sans-serif',
        fontWeight: config.typography?.fontWeight || 'normal',
        fontStyle: buildFontStyle(config.typography),
        lineHeight: config.typography?.lineHeight || 1.5,
        textAlign: config.alignment?.horizontal || 'left',
        // Backend wraps the whole text in an <a> when a link is set — hint that visually
        textDecoration:
          config.link?.enabled && config.link.url
            ? 'underline'
            : buildTextDecoration(config.typography),
        textTransform: config.typography?.textTransform || 'none',
        letterSpacing: config.typography?.letterSpacing ? `${config.typography.letterSpacing}px` : 'normal',
        paddingTop: `${config.padding?.top ?? 10}px`,
        paddingRight: `${config.padding?.right ?? 25}px`,
        paddingBottom: `${config.padding?.bottom ?? 10}px`,
        paddingLeft: `${config.padding?.left ?? 25}px`,
        minHeight: '20px', // Ensure block is visible even with no content
      }}
    >
      <div
        ref={contentRef}
        className={`text-block-wrapper outline-none ${isEditing ? 'cursor-text' : ''}`}
        contentEditable={isEditing}
        suppressContentEditableWarning
        onInput={handleInput}
        onPaste={handlePasteEvent}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
      />

      {/* Modal for editing an existing merge-variable pill */}
      <MergeVariableModal
        isOpen={editingMergeVar !== null}
        onClose={handleMergeVarClose}
        onSave={handleMergeVarSave}
        initialVariable={editingMergeVar?.variable}
        initialFallback={editingMergeVar?.fallback}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// DOM traversal helpers for pill-adjacent caret detection
// ---------------------------------------------------------------------------

/** Find the nearest previous sibling or parent's previous sibling (skipping ZWS text nodes). */
function findPreviousNode(node: Node): Node | null {
  let current: Node | null = node;
  // Walk previous siblings, skip zero-width-space-only text nodes
  while (current) {
    const prev: ChildNode | null = current.previousSibling;
    if (prev) {
      if (prev.nodeType === Node.TEXT_NODE && prev.textContent === '\u200B') {
        // Skip ZWS text nodes
        current = prev;
        continue;
      }
      return prev;
    }
    // Move up to parent and continue (but don't leave the contentEditable)
    current = current.parentNode;
    if (!current || (current as HTMLElement).contentEditable === 'true') return null;
  }
  return null;
}

/** Find the nearest next sibling or parent's next sibling (skipping ZWS text nodes). */
function findNextNode(node: Node): Node | null {
  let current: Node | null = node;
  while (current) {
    const next: ChildNode | null = current.nextSibling;
    if (next) {
      if (next.nodeType === Node.TEXT_NODE && next.textContent === '\u200B') {
        current = next;
        continue;
      }
      return next;
    }
    current = current.parentNode;
    if (!current || (current as HTMLElement).contentEditable === 'true') return null;
  }
  return null;
}
